import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { FaArrowUp, FaArrowDown, FaEye, FaBox } from "react-icons/fa";
import { getToken } from "../../api/auth";
import { getSalesHeaders } from "../../api/order";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./OrdersList.css";

const PAGE_SIZE = 50;

const OrdersList = () => {
  const token = getToken();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const loadMoreRef = useRef(null);

  // Sorting
  const [sortConfig, setSortConfig] = useState({
    key: "no",
    direction: "desc",
  });

  // Export modal
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFromDate, setExportFromDate] = useState("");
  const [exportToDate, setExportToDate] = useState("");

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 800);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const normalizeOrders = (rawOrders) => {
    return rawOrders.map((o) => {
      const saleslines = Array.isArray(o.saleslines) ? o.saleslines : [];
      const hasQuantityDifference = saleslines.some(
        (line) =>
          line.quantity != null &&
          line.originalQuantity != null &&
          Number(line.quantity) !== Number(line.originalQuantity)
      );
      return {
        no: o.no || "",
        selltocustomerno: o.sellToCustomerNo || "",
        selltocustomername: o.sellToCustomerName || "",
        selltoaddress: o.sellToAddress || "",
        selltocity: o.sellToCity || "",
        documentdate: o.documentDate || "",
        assigneduserid: o.assignedUserId || "",
        status: o.status ?? null,
        headeramount: o.headerAmount ?? 0,
        salesLinesCount: saleslines.length,
        hasQuantityDifference,
      };
    });
  };

  const getStatusName = (status) => {
    const map = {
      0: "Inserted",
      1: "Assigned to Agent",
      2: "Assigned to Controller",
      3: "Completed",
    };
    return map[status] || "Unknown";
  };

  const getStatusColor = (status) => {
    const colors = { 0: "#f59e0b", 1: "#3b82f6", 2: "#8910b9ff", 3: "#22c55e" };
    return colors[status] || "#ffffff";
  };

  // Map numeric status value to enum name for API
  const getStatusEnumName = (statusValue) => {
    const map = {
      0: "New",
      1: "AssignedToAgent",
      2: "AssignedToController",
      3: "Finished",
    };
    return map[statusValue] || statusValue;
  };

  const loadPage = useCallback(
    async (page, search = "", statusFilter = "All", isReset = false) => {
      if (!token) return;

      if (isReset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        // Convert numeric status to enum name for API
        const statusForApi =
          statusFilter === "All" ? "All" : getStatusEnumName(statusFilter);

        const res = await getSalesHeaders(
          page,
          PAGE_SIZE,
          search || undefined,
          statusForApi
        );

        if (res.success && Array.isArray(res.orders)) {
          const normalized = normalizeOrders(res.orders);

          setOrders((prev) => {
            if (isReset) {
              return normalized;
            }
            // Deduplicate: only add orders that don't already exist
            const existingNos = new Set(prev.map((o) => o.no));
            const newOrders = normalized.filter((o) => !existingNos.has(o.no));
            return [...prev, ...newOrders];
          });

          const hasMoreData = res.orders.length >= PAGE_SIZE;
          setHasMore(hasMoreData);
        } else {
          setHasMore(false);
        }
      } catch (err) {
        setError("Failed to load orders.");
        setHasMore(false);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [token]
  );

  // Reset & reload when search or status changes
  useEffect(() => {
    setOrders([]);
    setHasMore(true);
    setError(null);
    loadPage(1, debouncedSearch, statusFilter, true);
  }, [debouncedSearch, statusFilter, loadPage]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Client-side sort only (API handles status filtering)
  const displayedOrders = useMemo(() => {
    let list = [...orders];

    if (sortConfig.key) {
      list.sort((a, b) => {
        let A = a[sortConfig.key];
        let B = b[sortConfig.key];

        if (sortConfig.key === "hasQuantityDifference") {
          A = A ? 1 : 0;
          B = B ? 1 : 0;
        }
        if (typeof A === "string") A = A.toLowerCase();
        if (typeof B === "string") B = B.toLowerCase();

        return sortConfig.direction === "asc"
          ? A < B
            ? -1
            : 1
          : A > B
          ? -1
          : 1;
      });
    }

    return list;
  }, [orders, sortConfig]);

  // Infinite scroll
  useEffect(() => {
    if (!hasMore || loadingMore || !loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const nextPage = Math.floor(orders.length / PAGE_SIZE) + 1;
          loadPage(nextPage, debouncedSearch, statusFilter);
        }
      },
      { rootMargin: "300px" }
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [
    hasMore,
    loadingMore,
    orders.length,
    debouncedSearch,
    statusFilter,
    loadPage,
  ]);

  const handleExportWithDates = () => {
    let dataToExport = displayedOrders;

    if (exportFromDate || exportToDate) {
      dataToExport = dataToExport.filter((order) => {
        if (!order.documentdate) return false;
        const orderDate = new Date(order.documentdate);
        const from = exportFromDate ? new Date(exportFromDate) : null;
        const to = exportToDate ? new Date(exportToDate) : null;
        if (from) from.setHours(0, 0, 0, 0);
        if (to) to.setHours(23, 59, 59, 999);
        return (!from || orderDate >= from) && (!to || orderDate <= to);
      });
    }

    if (dataToExport.length === 0) {
      alert("No orders match the selected criteria.");
      return;
    }

    const exportData = dataToExport.map((order) => ({
      "Order No": order.no,
      "Customer Name": order.selltocustomername || "N/A",
      City: order.selltocity || "N/A",
      Address: order.selltoaddress || "N/A",
      "Document Date": order.documentdate
        ? new Date(order.documentdate).toLocaleDateString()
        : "-",
      "Assigned User": order.assigneduserid || "Unassigned",
      Status: getStatusName(order.status),
      "Amount (€)": Number(order.headeramount).toFixed(2),
      "Sales Lines": order.salesLinesCount,
      "Qty Diff": order.hasQuantityDifference ? "Yes" : "No",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    const fileName = `Orders_${exportFromDate || "All"}_to_${
      exportToDate || "All"
    }_${new Date().toISOString().slice(0, 10)}.xlsx`;
    saveAs(blob, fileName);
  };

  if (!token) return <Navigate to="/login" replace />;

  return (
    <div className="order-list-container">
      <div className="page-header">
        <h2 className="order-list-title">Order List</h2>
        <p className="page-subtitle">Manage warehouse orders</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="filters">
        <input
          type="text"
          placeholder="Search by order no, customer, city, address..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="All">All Status</option>
          <option value="0">Inserted</option>
          <option value="1">Assigned to Agent</option>
          <option value="2">Assigned to Controller</option>
          <option value="3">Completed</option>
        </select>

        <button
          className="glass-btn export-btn"
          onClick={() => setShowExportModal(true)}
        >
          Export To Excel
        </button>
        <button
          className="glass-btn add-agent-btn"
          onClick={() => navigate("/add-order")}
        >
          <FaBox size={20} /> Add Order
        </button>
      </div>

      <div className="order-table-container">
        {loading && orders.length === 0 ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading orders...</p>
          </div>
        ) : displayedOrders.length === 0 ? (
          <div className="empty-state">
            <p>No orders found</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <table className="order-table desktop-only">
              <thead>
                <tr>
                  {[
                    { key: "no", label: "Order No" },
                    { key: "selltocustomername", label: "Customer Name" },
                    { key: "selltocity", label: "City" },
                    { key: "selltoaddress", label: "Address" },
                    { key: "documentdate", label: "Document Date" },
                    { key: "assigneduserid", label: "Assigned User" },
                    { key: "status", label: "Status" },
                    { key: "headeramount", label: "Amount (€)" },
                    { key: "salesLinesCount", label: "Sales Lines" },
                    { key: "hasQuantityDifference", label: "Qty Diff" },
                  ].map(({ key, label }) => (
                    <th
                      key={key}
                      className="glass-header"
                      onClick={() => handleSort(key)}
                    >
                      {label}
                      {sortConfig.key === key && (
                        <span className="sort-icon">
                          {sortConfig.direction === "asc" ? (
                            <FaArrowUp />
                          ) : (
                            <FaArrowDown />
                          )}
                        </span>
                      )}
                    </th>
                  ))}
                  <th className="glass-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedOrders.map((order) => (
                  <tr key={order.no} className={`row-animate`}>
                    <td>{order.no}</td>
                    <td>{order.selltocustomername || "N/A"}</td>
                    <td>{order.selltocity || "N/A"}</td>
                    <td>{order.selltoaddress || "N/A"}</td>
                    <td>
                      {order.documentdate
                        ? new Date(order.documentdate).toLocaleDateString()
                        : "-"}
                    </td>
                    <td>{order.assigneduserid || "Unassigned"}</td>
                    <td>
                      <span
                        className="status-indicator"
                        style={{
                          backgroundColor: getStatusColor(order.status),
                        }}
                      ></span>
                      {getStatusName(order.status)}
                    </td>
                    <td>€{Number(order.headeramount).toFixed(2)}</td>
                    <td>{order.salesLinesCount}</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={order.hasQuantityDifference}
                        readOnly
                      />
                    </td>
                    <td>
                      <Link
                        to={`/orders/${order.no}`}
                        className="glass-btn btn-primary"
                      >
                        <FaEye size={16} /> View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="order-cards mobile-only">
              {displayedOrders.map((order) => (
                <div key={order.no} className={`order-card row-animate`}>
                  <div className="order-card-field">
                    <strong>Order No:</strong> <span>{order.no}</span>
                  </div>
                  <div className="order-card-field">
                    <strong>Customer:</strong>{" "}
                    <span>{order.selltocustomername || "N/A"}</span>
                  </div>
                  <div className="order-card-field">
                    <strong>City:</strong>{" "}
                    <span>{order.selltocity || "N/A"}</span>
                  </div>
                  <div className="order-card-field">
                    <strong>Address:</strong>{" "}
                    <span>{order.selltoaddress || "N/A"}</span>
                  </div>
                  <div className="order-card-field">
                    <strong>Document Date:</strong>{" "}
                    <span>
                      {order.documentdate
                        ? new Date(order.documentdate).toLocaleDateString()
                        : "-"}
                    </span>
                  </div>
                  <div className="order-card-field">
                    <strong>Assigned User:</strong>{" "}
                    <span>{order.assigneduserid || "Unassigned"}</span>
                  </div>
                  <div className="order-card-field">
                    <strong>Status:</strong>{" "}
                    <span>{getStatusName(order.status)}</span>
                  </div>
                  <div className="order-card-field">
                    <strong>Amount:</strong>{" "}
                    <span>€{Number(order.headeramount).toFixed(2)}</span>
                  </div>
                  <div className="order-card-field">
                    <strong>Sales Lines:</strong>{" "}
                    <span>{order.salesLinesCount}</span>
                  </div>
                  <div className="order-card-field">
                    <strong>Qty Diff:</strong>{" "}
                    <input
                      type="checkbox"
                      checked={order.hasQuantityDifference}
                      readOnly
                      style={{ cursor: "not-allowed" }}
                    />
                  </div>
                  <Link
                    to={`/orders/${order.no}`}
                    className="glass-btn btn-primary"
                  >
                    <FaEye size={16} /> View
                  </Link>
                </div>
              ))}
            </div>

            {loadingMore ? (
              <div className="load-more-container loading">
                <div className="spinner"></div>
                <p>Loading more orders...</p>
              </div>
            ) : hasMore ? (
              // Invisible trigger for auto-scroll (with optional subtle loading hint)
              <div ref={loadMoreRef} className="load-more-container">
                <div
                  className="spinner"
                  style={{ opacity: 0.5, display: "none" }}
                ></div>
                <p style={{ opacity: 0.5, fontSize: "0.9em" }}>
                  Approaching end of list...
                </p>
              </div>
            ) : orders.length > 0 ? (
              <div className="load-more-container end">
                <p>All orders loaded</p>
              </div>
            ) : null}

            {!hasMore && orders.length > 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: "1rem",
                  color: "rgba(255,255,255,0.6)",
                }}
              >
                No more orders to load
              </div>
            )}
          </>
        )}
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowExportModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Export Orders to Excel</h3>
            <p className="modal-subtitle">
              Select a date range based on <strong>Document Date</strong>
            </p>
            <div className="modal-date-inputs">
              <div className="date-field">
                <label>From Date</label>
                <input
                  type="date"
                  value={exportFromDate}
                  onChange={(e) => setExportFromDate(e.target.value)}
                  className="date-input"
                />
              </div>
              <div className="date-field">
                <label>To Date</label>
                <input
                  type="date"
                  value={exportToDate}
                  onChange={(e) => setExportToDate(e.target.value)}
                  className="date-input"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="glass-btn cancel-btn"
                onClick={() => {
                  setShowExportModal(false);
                  setExportFromDate("");
                  setExportToDate("");
                }}
              >
                Cancel
              </button>
              <button
                className="glass-btn export-confirm-btn"
                onClick={() => {
                  handleExportWithDates();
                  setShowExportModal(false);
                  setExportFromDate("");
                  setExportToDate("");
                }}
                disabled={displayedOrders.length === 0}
              >
                Export
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersList;
